import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { DownloadForOffline } from "@mui/icons-material";
import { AnomalyRepStatus, themes, optionalAvathar } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { useDispatch } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { InfoMessage } from "components/CustomComponents/CustomButton";

const AnomaliesReport = ({ setShowReport }) => {
  const {
    domain,
    token,
    userDetails,
    globalSearchValue,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anomalyRepDetails, setAnomalyRepDetails] = useState([]);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "days").format("YYYY-MM-DD")
  );
  const [anoSts, setAnoSts] = useState("All");
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}`
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState("99999");

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const anostatus = anoSts === "All" ? "" : anoSts;
        let searchStartDate = startDate.split("/").reverse().join("-");

        const response = await fetch(
          `${domain}anomalies-report/?user_id=${userDetails.id}&date=${searchStartDate}&status=${anostatus}&department_id=${departmentId}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setAnomalyRepDetails(res?.data);
          setMasterDataForSearch(res?.data);
        } else if (response.status === 409) {
          setAnomalyRepDetails([]);
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setAnomalyRepDetails([]);
          setMasterDataForSearch([]);
          throw new Error(res.error);
        }
      } catch (error) {
        setAnomalyRepDetails([]);
        setMasterDataForSearch([]);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    anoSts,
    departmentId,
    userDetails.id,
    topbarCompanyId,
  ]);

  useEffect(() => {
    const filteredResults = masterDataForSearch?.filter((item) =>
      [
        "username",
        "shift_name",
        "shift_start_time",
        "check_in_time",
        "shift_end_time",
        "check_out_time",
        "status",
        "late_hours",
      ].some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setAnomalyRepDetails(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getMaxDate = () => {
    const today = new Date();
    today.setDate(today.getDate() - 1);
    return today.toISOString().split("T")[0];
  };

  const handleDateChange = (event) => {
    if (event.target.value) {
      const selectedDate = event.target.value;
      const maxDate = getMaxDate();

      if (selectedDate <= maxDate) {
        setStartDate(selectedDate);
      } else {
        setStartDate(maxDate);
      }
    }
  };

  const getAnomaliesReport = () => {
    let dataPreparation = [];
    anomalyRepDetails?.forEach((element) => {
      let overAllData = {
        employeename: element?.username,
        employeeid: element?.employee_id,
        shiftname: element?.shift_name ? element?.shift_name : "Unassigned",
        shiftstarttime: element?.shift_start_time
          ? `${element?.shift_start_time} - ${element?.shift_end_time}`
          : "N/A",
        checkin: element?.check_in_time ? element?.check_in_time : "N/A",
        checkout: element?.check_out_time ? element?.check_out_time : "N/A",
        status: element?.status ? element?.status : "N/A",
        latehours: element?.late_hours ? element?.late_hours : "N/A",
      };
      dataPreparation = [...dataPreparation, overAllData];
    });
    const AnomalyReport = ["Attendance Anomalies Report"];
    const Space = [""];
    const Dates = ["Date"];
    const companyName = ["Company Name"];
    const Department = ["Department Name"];
    const Status = ["Status"];
    const GeneratedOn = ["Report Generated On"];
    AnomalyReport.push("");
    Dates.push(moment(startDate).format("DD/MM/YYYY"));
    companyName.push(topbarCompanyName);
    Department.push(departmentName);
    Status.push(anoSts);
    GeneratedOn.push(moment(new Date()).format("DD/MM/YYYY, hh:mm a"));
    Space.push("");
    const items = dataPreparation;
    const TableHead = ["Employee Id"];
    TableHead.push("Employee Name");
    TableHead.push("Shift Name");
    TableHead.push("Shift Time");
    TableHead.push("Check In");
    TableHead.push("Check Out");
    TableHead.push("Status");
    TableHead.push("Late Hours");
    const replacer = (key, value) => (value === null ? "" : value);
    const header = Object.keys(items[0]);
    let csv = items.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(",")
    );
    csv.unshift(TableHead.join(","));
    csv.unshift(Space.join(","));
    csv.unshift(GeneratedOn.join(","));
    csv.unshift(Status.join(","));
    csv.unshift(Department.join(","));
    csv.unshift(companyName.join(","));
    csv.unshift(Dates.join(","));
    csv.unshift(AnomalyReport.join(","));
    csv = csv.join("\r\n");
    const ConvertData = csv;
    const element = document.createElement("a");
    const file = new Blob([ConvertData], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "AttendanceAnomaliesReport.csv";
    document.body.appendChild(element);
    element.click();
  };

  return (
    <React.Fragment>
      <Mui.Grid container spacing={1} sx={{ mt: 0 }}>
        <Mui.Grid item xs={12} sm={12} md={12} lg={12}>
          <Mui.Stack direction="row">
            <BackArrow
              actionFunction={() => {
                dispatch(setGlobalSearchValue(""));
                setShowReport(false);
              }}
            />
            <Mui.Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                mt: 1.5,
              }}
            >
              Attendance Anomalies Report
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Grid>
      </Mui.Grid>
      <Mui.Grid container spacing={1} sx={{ mt: 1, mb: 1 }}>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <Mui.TextField
            id="from-date"
            type="date"
            size="small"
            label="Select Date"
            value={startDate}
            sx={{ background: themes.whiteColor }}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={handleDateChange}
            fullWidth
            disableOpenPicker
            inputProps={{
              max: getMaxDate(),
            }}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <Mui.Autocomplete
            disablePortal
            id="combo-box-demo"
            size="small"
            sx={{ minWidth: "100%" }}
            value={
              [{ id: 99999, department_name: "All" }, ...departmentData]?.find(
                (option) => option?.department_name === departmentName
              ) || {
                department_name: departmentName,
              }
            }
            options={[{ id: 99999, department_name: "All" }, ...departmentData]}
            ListboxProps={{
              style: {
                maxHeight: "150px",
              },
            }}
            filterOptions={(options, state) => {
              return options?.filter((option) =>
                option?.department_name
                  ?.toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              );
            }}
            getOptionLabel={(option) =>
              option?.department_name ? option?.department_name : ""
            }
            isOptionEqualToValue={(option, value) =>
              option.department_name === value.department_name
            }
            renderOption={(props, option) => {
              return <li {...props}>{option?.department_name}</li>;
            }}
            onChange={(event, value) => {
              if (value && value?.department_name) {
                setDepartmentName(value.department_name);
                setDepartmentId(value.id);
              }
            }}
            renderInput={(params) => (
              <Mui.TextField
                {...params}
                size="small"
                label="Department"
                sx={{ background: "white" }}
                name="department name"
                placeholder="Select Department"
              />
            )}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2}>
          <Mui.Autocomplete
            disablePortal
            id="combo-box-demo"
            size="small"
            sx={{ minWidth: "100%" }}
            value={
              AnomalyRepStatus?.find((option) => option?.label === anoSts) ||
              anoSts ||
              ""
            }
            options={AnomalyRepStatus}
            ListboxProps={{
              style: {
                maxHeight: "150px",
              },
            }}
            filterOptions={(options, state) => {
              return options.filter((option) =>
                option.label
                  ?.toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              );
            }}
            renderOption={(props, option) => {
              return <li {...props}>{option.label}</li>;
            }}
            onChange={(event, value) => {
              if (value && value?.label) {
                setAnoSts(value.label);
              }
            }}
            renderInput={(params) => (
              <Mui.TextField
                {...params}
                size="small"
                label="Status"
                sx={{ background: "white" }}
                name="status"
                placeholder="Select Status"
              />
            )}
          />
        </Mui.Grid>
        <Mui.Grid item lg={5.2}></Mui.Grid>
        <Mui.Grid item xs={2} sm={2} md={2} lg={0.5}>
          <Mui.IconButton
            onClick={() => {
              getAnomaliesReport();
            }}
            disabled={anomalyRepDetails?.length === 0 ? true : false}
          >
            <DownloadForOffline
              size="large"
              sx={{ color: themes.downloadIconColor, fontSize: 40, mt: -1 }}
            />
          </Mui.IconButton>
        </Mui.Grid>
      </Mui.Grid>

      {isLoading ? (
        <Loader info="Loading..." />
      ) : (
        <>
          <Mui.Grid container sx={{ overflow: "hidden", mt: 2 }}>
            <Mui.TableContainer
              sx={{ mt: 2, width: "100%", maxHeight: 675, ml: 2, mr: 2 }}
            >
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 100,
                  }}
                >
                  <Mui.TableRow
                    maxWidth="xl"
                    align="left"
                    sx={{
                      "& .MuiTableCell-root": {
                        height: "15px",
                        padding: "3px 15px",
                      },
                    }}
                  >
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "18%",
                      }}
                    >
                      Employee Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "18%",
                      }}
                    >
                      Employee Id
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "18%",
                      }}
                    >
                      Shift Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "20%",
                      }}
                    >
                      Shift Time
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                      }}
                    >
                      Check In
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                      }}
                    >
                      Check Out
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "14%",
                      }}
                    >
                      Status
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                      }}
                    >
                      Late Hours
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {anomalyRepDetails?.length === 0 ? (
                    <InfoMessage dataLength={masterDataForSearch} />
                  ) : (
                    anomalyRepDetails
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((item, index) => (
                        <Mui.TableRow
                          key={item?.id}
                          sx={{
                            "& .MuiTableCell-root": {
                              height: "10px",
                              padding: "3px 15px",
                            },
                          }}
                        >
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title={item?.username}
                              disableHoverListener={!item?.username}
                            >
                              <Mui.Stack direction="Row">
                                <Mui.Avatar
                                  sx={{ width: 23, height: 23 }}
                                  alt="Remy Sharp"
                                  src={item?.image_url ?? optionalAvathar}
                                />
                                <Mui.Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    paddingLeft: 1,
                                  }}
                                >
                                  {item?.username}
                                </Mui.Typography>
                              </Mui.Stack>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.employee_id ? item?.employee_id : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title={item?.shift_name}
                              disableHoverListener={!item?.shift_name}
                            >
                              <Mui.Typography
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item?.shift_name
                                  ? item?.shift_name
                                  : "Unassigned"}
                              </Mui.Typography>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.shift_start_time
                                ? item?.shift_start_time
                                : "N/A"}{" "}
                              -{" "}
                              {item?.shift_end_time
                                ? item?.shift_end_time
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.check_in_time
                                ? item?.check_in_time
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.check_out_time
                                ? item?.check_out_time
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.status ? item?.status : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.late_hours ? item?.late_hours : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Grid>

          {anomalyRepDetails?.length > 25 && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[25, 50, 75, 100]}
              count={anomalyRepDetails?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default AnomaliesReport;
